// ---------------------------------------------------------------------------
// World scale
// ---------------------------------------------------------------------------
// The view looks at the mountainside from a low angle: up the screen is up the
// mountain. One screen holds the whole hill. The valley floor and the reservoir
// are at the bottom, the Cerro del Caballo ridge and the assembly point at the
// top.

/** Logical pixels per simulation cell. Terrain is drawn at cell resolution. */
export const CELL = 4;
export const GRID_W = 120;
export const GRID_H = 75;
export const N_CELLS = GRID_W * GRID_H;
export const LOGICAL_W = GRID_W * CELL;
export const LOGICAL_H = GRID_H * CELL;

/** Simulation timestep. 30 Hz, fixed. */
export const TICK = 1 / 30;

/** Metres per cell, used only for the "hectares burnt" readout. */
export const METRES_PER_CELL = 18;

export const idx = (x: number, y: number): number => y * GRID_W + x;
export const inBounds = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < GRID_W && y < GRID_H;

// ---------------------------------------------------------------------------
// Terrain
// ---------------------------------------------------------------------------

export enum T {
  Sky = 0,
  Rock = 1,
  Water = 2,
  Road = 3,
  Grass = 4,
  Scrub = 5,
  Pine = 6,
  Olive = 7,
  Building = 8,
  Helipad = 9,
  Base = 10,
  /**
   * An acequia: one of the old open irrigation channels. Nothing grows in it, so
   * it slows the fire down, but it is narrow enough to step across.
   */
  Acequia = 11,
}

// ---------------------------------------------------------------------------
// Fire behaviour classes
// ---------------------------------------------------------------------------

/**
 * How tall the flames are, in the four bands used on real wildfires. Flame
 * height is what decides who is allowed to do what. Real crews are taught these
 * same limits, so when the game refuses an order it is teaching the real rule.
 */
export enum Flame {
  /** Under about 1.2 m. Crews on foot and engines can attack the edge directly. */
  Low = 0,
  /** 1.2 to 2.4 m. Too hot for crews on foot; engines and aircraft still work. */
  Moderate = 1,
  /**
   * 2.4 to 3.4 m. Single trees flare up and embers start new fires ahead of the
   * flames. Attacking the head, the fast-moving front edge, will fail.
   */
  High = 2,
  /**
   * Over 3.4 m. Fire runs through the treetops and covers long distances
   * quickly. Nothing can stop the head.
   */
  Extreme = 3,
}

export const flameClass = (intensity: number): Flame =>
  intensity < 0.25 ? Flame.Low : intensity < 0.5 ? Flame.Moderate : intensity < 0.75 ? Flame.High : Flame.Extreme;

/** Rough flame length in metres, for the radio and the readouts. */
export const flameMetres = (intensity: number): number => 0.4 + intensity * 4.2;

export const FLAME_NAME: readonly string[] = ['low', 'moderate', 'high', 'extreme'];

/** The hottest edge an engine crew may attack directly. */
export const ENGINE_DIRECT_LIMIT = Flame.Moderate;

export interface TerrainDef {
  name: string;
  /** Initial fuel load. 0 means it cannot burn. */
  fuel: number;
  /** Accumulated heat needed to ignite, before moisture. */
  ign: number;
  /** Fuel consumed per second while burning. */
  burn: number;
  /** Heat emission multiplier while burning. */
  emit: number;
  /** Base moisture (0..1). */
  moist: number;
  /** Walking cost multiplier. Infinity = impassable on foot. */
  walk: number;
}

/**
 * Indexed by T. Tuned so that, if nobody fights it, fire spreads up the hill in
 * about five minutes.
 */
export const TERRAIN: readonly TerrainDef[] = [
  { name: 'sky', fuel: 0, ign: 9, burn: 0, emit: 0, moist: 0, walk: Infinity },
  { name: 'rock', fuel: 0, ign: 9, burn: 0, emit: 0, moist: 0, walk: 1.9 },
  { name: 'water', fuel: 0, ign: 9, burn: 0, emit: 0, moist: 0, walk: Infinity },
  { name: 'road', fuel: 0, ign: 9, burn: 0, emit: 0, moist: 0, walk: 0.7 },
  // burn = fuel consumed per second, so fuel/burn is roughly how many seconds a
  // cell stays alight. Long burns matter: an edge that keeps giving off heat is
  // what lets fire creep slowly downhill instead of only ever running uphill.
  { name: 'dry grass', fuel: 0.35, ign: 0.3, burn: 0.035, emit: 1.3, moist: 0.12, walk: 1.0 },
  { name: 'matorral', fuel: 0.7, ign: 0.42, burn: 0.027, emit: 1.15, moist: 0.2, walk: 1.4 },
  { name: 'pine', fuel: 1.0, ign: 0.62, burn: 0.018, emit: 1.45, moist: 0.3, walk: 1.2 },
  // Watered terraces slow the fire down. They deliberately do not stop it.
  { name: 'terrace', fuel: 0.45, ign: 0.5, burn: 0.025, emit: 0.75, moist: 0.28, walk: 1.05 },
  { name: 'building', fuel: 0.9, ign: 0.7, burn: 0.02, emit: 0.9, moist: 0.35, walk: 1.5 },
  { name: 'helipad', fuel: 0, ign: 9, burn: 0, emit: 0, moist: 0, walk: 0.8 },
  { name: 'base', fuel: 0, ign: 9, burn: 0, emit: 0, moist: 0, walk: 0.8 },
  { name: 'acequia', fuel: 0, ign: 9, burn: 0, emit: 0, moist: 0, walk: 2.2 },
];

export enum CellState {
  Unburnt = 0,
  Burning = 1,
  Burnt = 2,
}

export interface Grid {
  terrain: Uint8Array;
  state: Uint8Array;
  /**
   * Heat a cell takes in before it catches. It comes from burning neighbours,
   * and it fades on its own.
   */
  heat: Float32Array;
  /**
   * Scratch space where one tick's heat is collected, so the result does not
   * depend on the order the cells are scanned in.
   */
  heatAdd: Float32Array;
  moist: Float32Array;
  fuel: Float32Array;
  /** Derived flame intensity (0..1) for burning cells. */
  inten: Float32Array;
  /** One random byte per cell, fixed when the map is made. It varies how each cell is drawn. */
  tex: Uint8Array;
}

export function makeGrid(): Grid {
  return {
    terrain: new Uint8Array(N_CELLS),
    state: new Uint8Array(N_CELLS),
    heat: new Float32Array(N_CELLS),
    heatAdd: new Float32Array(N_CELLS),
    moist: new Float32Array(N_CELLS),
    fuel: new Float32Array(N_CELLS),
    inten: new Float32Array(N_CELLS),
    tex: new Uint8Array(N_CELLS),
  };
}

/** The eight neighbours, with a distance weight for diagonals. */
export const NEIGHBOURS: readonly { dx: number; dy: number; w: number }[] = [
  { dx: -1, dy: -1, w: 0.7 },
  { dx: 0, dy: -1, w: 1 },
  { dx: 1, dy: -1, w: 0.7 },
  { dx: -1, dy: 0, w: 1 },
  { dx: 1, dy: 0, w: 1 },
  { dx: -1, dy: 1, w: 0.7 },
  { dx: 0, dy: 1, w: 1 },
  { dx: 1, dy: 1, w: 0.7 },
];

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export enum PState {
  /** At home, unaware. */
  Home = 0,
  /** Alerted, heading for a road access point. */
  ToRoad = 1,
  /** In a car queueing down the mountain. */
  InCar = 2,
  /** On foot, climbing to the assembly point. */
  Walking = 3,
  /** At the assembly point, waiting for a lift. */
  Waiting = 4,
  /** Aboard the helicopter. */
  Aboard = 5,
  /** Drove off the mountain. */
  Safe = 6,
  /** Flown off the mountain. */
  Rescued = 7,
  Lost = 8,
}

export const RESOLVED = (s: PState): boolean => s >= PState.Safe;

export interface Person {
  id: number;
  /** Position in cell units (float). */
  x: number;
  y: number;
  houseId: number;
  /** Family group. Everyone in a group shares one car. */
  group: number;
  state: PState;
  /** Base walking speed in cells/second. */
  speed: number;
  health: number;
  /** Refuses to leave until fire is close. */
  stubborn: boolean;
  carId: number;
  /** Which road they are heading for (-1 = none / going up). */
  roadId: number;
  /** Seconds since they last reconsidered their plan. */
  think: number;
}

// ---------------------------------------------------------------------------
// Roads and traffic
// ---------------------------------------------------------------------------

export interface RoadCell {
  x: number;
  y: number;
  /** Speed limit in cells/second. Hairpin bends are slow, and that is where queues form. */
  limit: number;
}

export interface Road {
  id: number;
  name: string;
  /** Ordered from the top of the mountain (s = 0) to the exit (s = cells.length - 1). */
  cells: RoadCell[];
  /** Lowest s at which fire blocks the road, or null when clear. */
  cut: number | null;
  /** For each s, the nearest impassable s at or ahead of it (Infinity if clear). */
  blocked: Float32Array;
  /** For each s, the nearest impassable s at or behind it, because engines drive uphill. */
  blockedBehind: Float32Array;
  /**
   * For each s, the nearest s at or ahead that is burning or getting very hot.
   * This is the escape-route warning: it means fire between a crew and the
   * valley.
   */
  riskAhead: Float32Array;
  /** For each s, 1 if the fire has already been through. Still passable, but slow. */
  scorched: Uint8Array;
  /** 0..1, how jammed the road is. */
  jam: number;
  /** Cars that have driven off the map by this road. */
  through: number;
}

export interface Car {
  id: number;
  roadId: number;
  /** Distance along the road in cells. */
  s: number;
  v: number;
  occupants: number[];
  /** Seconds spent unable to move. */
  stalled: number;
  /** Seconds still held at the pickup waiting for the rest of the family. */
  boarding: number;
}

// ---------------------------------------------------------------------------
// Buildings
// ---------------------------------------------------------------------------

export interface House {
  id: number;
  villageId: number;
  name: string;
  /** Top-left cell of the building footprint. */
  x: number;
  y: number;
  w: number;
  h: number;
  residents: number;
  roadId: number;
  /** s position of the nearest road access point. */
  accessS: number;
  accessX: number;
  accessY: number;
}

// ---------------------------------------------------------------------------
// Effects
// ---------------------------------------------------------------------------

export interface Drop {
  x: number;
  y: number;
  vx: number;
  vy: number;
  t: number;
  fall: number;
  amount: number;
  radius: number;
}

export enum PKind {
  Smoke = 0,
  Ember = 1,
  Splash = 2,
  Steam = 3,
  Cheer = 4,
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  kind: PKind;
  seed: number;
}
